import React, { useEffect, useState } from 'react';
import { Button, Form, Input } from 'antd';
import {
  DeleteOutlined,
  DesktopOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import useGetInfoDevice from './services/hooks/useGetInfoDevice';
import { useParams } from 'react-router-dom';
import accountApis from 'services/accountApis';
import Text from 'components/Text';

interface DevicePayload {
  id: string;
  description: string;
  bitBlocker: string;
  ultraViewerId: string;
  ultraViewerPass: string;
  stationNumber: string;
}

const DeviceInformation = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const [isLoading, setLoading] = useState(false);

  const { data, loading, refetch } = useGetInfoDevice(id || '');

  const handleFinish = async (values: { devices: DevicePayload[] }) => {
    setLoading(true);
    try {
      const res = await accountApis.updateInfoDevice(id || '', values.devices);

      if (res.status === 200) {
        refetch();
      } else {
        console.error('Failed to update device information');
      }
    } catch (error) {
      console.error('Error updating device information:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        devices: data,
      });
    }
  }, [data, form]);

  const handleReset = () => {
    if (!data) return;

    form.setFieldsValue({
      devices: data,
    });
  };

  const formatBitBlocker = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 48);
    return digits.match(/.{1,6}/g)?.join('-') || '';
  };

  return (
    <Container>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleFinish}
        initialValues={{ devices: data || [] }}
      >
        <Header>
          <HeaderContent>
            <Title>Device Information</Title>

            <Subtitle>
              Manage computer devices, BitLocker keys and remote access
              information for your salon.
            </Subtitle>
          </HeaderContent>

          <AddButton
            type='primary'
            icon={<PlusOutlined />}
            disabled={loading || isLoading}
            onClick={() => {
              const devices = form.getFieldValue('devices') || [];

              form.setFieldsValue({
                devices: [
                  ...devices,
                  {
                    stationNumber: String(devices.length + 1),
                    description: '',
                    bitBlocker: '',
                    ultraViewerId: '',
                    ultraViewerPass: '',
                  },
                ],
              });
            }}
          >
            Add Device
          </AddButton>
        </Header>

        <Form.List name='devices'>
          {(fields, { remove, add }) =>
            fields.length === 0 ? (
              <EmptyDevice>
                <Text variant='BODY_1'>No devices added yet</Text>

                <Text variant='CAPTION_1' color='text_2' mb={2}>
                  Add a device to manage its station number, BitLocker recovery
                  key, and remote access information.
                </Text>

                <AddButton
                  type='primary'
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({
                      stationNumber: String(fields.length + 1),
                      description: '',
                      bitBlocker: '',
                      ultraViewerId: '',
                      ultraViewerPass: '',
                    })
                  }
                  disabled={loading || isLoading}
                >
                  Add Device
                </AddButton>
              </EmptyDevice>
            ) : (
              <DeviceList>
                {fields.map((field, index) => (
                  <DeviceCard key={field.key}>
                    <DeviceCardHeader>
                      <DeviceInfo>
                        <DeviceIcon>
                          <DesktopOutlined />
                        </DeviceIcon>

                        <DeviceTitleWrapper>
                          <StationTitle>Station #
                            <Form.Item noStyle shouldUpdate>
                              {({ getFieldValue }) => {
                                const stationNumber = getFieldValue([
                                  'devices',
                                  field.name,
                                  'stationNumber',
                                ]);

                                return stationNumber;
                              }}
                            </Form.Item>
                          </StationTitle>

                          <StationDescription>
                            <Form.Item noStyle shouldUpdate>
                              {({ getFieldValue }) => {
                                const description = getFieldValue([
                                  'devices',
                                  field.name,
                                  'description',
                                ]);

                                return description || 'Station';
                              }}
                            </Form.Item>
                          </StationDescription>
                        </DeviceTitleWrapper>
                      </DeviceInfo>

                      <DeleteButton
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                        disabled={loading || isLoading}
                      >
                        Delete
                      </DeleteButton>
                    </DeviceCardHeader>

                    <DeviceCardBody>
                      <Form.Item name={[field.name, 'id']} hidden>
                        <Input />
                      </Form.Item>
                      <Row>
                        <FormItem
                          label='Station Number'
                          name={[field.name, 'stationNumber']}
                          rules={[
                            {
                              required: true,
                              message: 'Please enter station number',
                            },
                          ]}
                        >
                          <Input placeholder='01' />
                        </FormItem>

                        <FormItem
                          label='Description'
                          name={[field.name, 'description']}
                          rules={[
                            {
                              required: true,
                              message: 'Please enter description',
                            },
                          ]}
                        >
                          <Input placeholder='Front Desk' />
                        </FormItem>
                      </Row>

                      <FormItem
                        label={
                          <BitLockerLabel>
                            BitLocker Recovery Key
                            <span> (48 digits total)</span>
                          </BitLockerLabel>
                        }
                        name={[field.name, 'bitBlocker']}
                        rules={[
                          {
                            required: true,
                            message: 'Please enter BitLocker recovery key',
                          },
                          {
                            validator: (_, value) => {
                              if (!value) {
                                return Promise.resolve();
                              }

                              const digitsOnly = value.replace(/\D/g, '');

                              if (digitsOnly.length !== 48) {
                                return Promise.reject(
                                  new Error(
                                    'BitLocker key must contain exactly 48 digits',
                                  ),
                                );
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          placeholder='123456-123456-123456-123456-123456-123456-123456-123456'
                          maxLength={55}
                          onChange={(e) => {
                            const formatted = formatBitBlocker(e.target.value);
                            form.setFieldValue(
                              ['devices', field.name, 'bitBlocker'],
                              formatted,
                            );
                          }}
                        />
                      </FormItem>

                      <Row>
                        <FormItem
                          label='UltraViewer ID'
                          name={[field.name, 'ultraViewerId']}
                        >
                          <Input 
                            placeholder='123 456 789' 
                            onChange={(e) => {
                              const formatted = e.target.value.replace(/\D/g, '').slice(0, 9);
                              const spaced = formatted.replace(/(\d{3})(?=\d)/g, '$1 ');
                              form.setFieldValue(
                                ['devices', field.name, 'ultraViewerId'],
                                spaced,
                              );
                            }}
                          />
                        </FormItem>

                        <FormItem
                          label='UltraViewer Password'
                          name={[field.name, 'ultraViewerPass']}
                        >
                          <Input.Password
                            placeholder='Enter password'
                            iconRender={(visible) =>
                              visible ? (
                                <EyeOutlined />
                              ) : (
                                <EyeInvisibleOutlined />
                              )
                            }
                          />
                        </FormItem>
                      </Row>
                    </DeviceCardBody>
                  </DeviceCard>
                ))}
              </DeviceList>
            )
          }
        </Form.List>

        <Footer>
          <CancelButton onClick={handleReset} disabled={loading || isLoading}>
            Reset
          </CancelButton>

          <SaveButton
            type='primary'
            htmlType='submit'
            loading={loading || isLoading}
          >
            Save
          </SaveButton>
        </Footer>
      </Form>
    </Container>
  );
};

export default DeviceInformation;

const Container = styled.div`
  width: 100%;

  .ant-form {
    font-family: Poppins;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h2`
  margin: 0;
  color: #1d2129;
  font-size: 28px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #667085;
  font-size: 16px;
  line-height: 20px;
`;

const AddButton = styled(Button)`
  height: 40px;
  padding: 0 14px;
  border-radius: 5px;

  font-size: 14px;
  font-weight: 500;
  font-family: Poppins;
  display: flex;
  align-items: center;
`;

const DeviceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  padding-right: 10px;

  overflow-y: auto;
  max-height: calc(100vh - 500px);

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;
const DeviceCard = styled.div`
  width: 100%;
  background: #fff;
  border: 1px solid #edf0f5;
  border-radius: 7px;

  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.04);
`;

const DeviceCardHeader = styled.div`
  min-height: 52px;
  padding: 10px 16px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  background: linear-gradient(90deg, #f4f7fc 0%, #f8faff 100%);

  border-bottom: 1px solid #edf0f5;
`;

const DeviceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DeviceIcon = styled.div`
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  background: #e8f1ff;

  color: #1264e8;
  font-size: 18px;
`;

const DeviceTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const StationTitle = styled.div`
  color: #1d2129;
  font-size: 16px;
  line-height: 18px;
  font-weight: 600;
`;

const StationDescription = styled.div`
  color: #667085;
  font-size: 14px;
  line-height: 16px;
`;

const DeleteButton = styled(Button)`
  height: 30px;
  padding: 0 12px;
  border-radius: 5px;
  font-size: 14px;
  font-family: Poppins;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DeviceCardBody = styled.div`
  padding: 10px 14px 8px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormItem = styled(Form.Item)`
  margin-bottom: 8px;

  .ant-form-item-label {
    padding-bottom: 3px;

    > label {
      height: auto;
      font-family: Poppins;
      color: #344054;
      font-size: 14px;
      line-height: 18px;
      font-weight: 500;
    }
  }

  .ant-input,
  .ant-input-affix-wrapper {
    height: 40px;

    border-radius: 5px;
    border-color: #d9dee8;

    font-family: Poppins;
    font-size: 16px;

    &:hover,
    &:focus {
      border-color: #1677ff;
    }
  }

  .ant-input-affix-wrapper {
    padding: 0 9px;

    height: 40px;

    .ant-input {
      height: 100%;
    }
  }
`;

const BitLockerLabel = styled.span`
  span {
    color: #ff4d4f;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  margin-top: 18px;
`;

const CancelButton = styled(Button)`
  min-width: 124px;
  height: 40px;
  border: none;
  background: #edf1f6;
  font-family: Poppins;
  color: #344054;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: #e3e8ef !important;
    color: #344054 !important;
  }
`;

const SaveButton = styled(Button)`
  min-width: 135px;
  height: 40px;

  font-size: 14px;
  border-radius: 5px;
  font-weight: 500;
  font-family: Poppins;
`;

const EmptyDevice = styled.div`
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  gap: 8px;
  text-align: center;
`;
